import * as React from 'react';
import { Link } from 'react-router-dom';

import fwcLogo from '../../../../../public/brand/fwc-logo.png';
import { appIdentity } from '@/app/app-config';
import { Button } from '@/components/ui/button';
import { PublicAuthNotices, PublicSignInButton, usePublicSignIn } from './public-sign-in';

function useDocumentTitle(title: string) {
  React.useEffect(() => {
    const previous = document.title;
    document.title = `${title} · ${appIdentity.name}`;
    return () => { document.title = previous; };
  }, [title]);
}

function SignedOutFrame({ children }: { children: React.ReactNode }) {
  return (
    <div className="bg-background text-foreground flex min-h-svh flex-col items-center justify-center px-4 py-10">
      <main id="public-main" tabIndex={-1} className="w-full max-w-md space-y-6 outline-none">
        <img src={fwcLogo} alt="Fair Work Commission" className="h-auto w-full max-w-60" />
        {children}
      </main>
    </div>
  );
}

/** Shown at `/` to signed-out visitors when public visitor pages are turned off. */
export function PublicSignInScreen() {
  const action = usePublicSignIn();
  useDocumentTitle('Sign in');
  return (
    <SignedOutFrame>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Sign in to {appIdentity.name}</h1>
        <p className="text-muted-foreground text-sm">Use your work account to continue.</p>
      </div>
      <PublicSignInButton action={action} size="lg" />
      <PublicAuthNotices signInError={action.error} />
    </SignedOutFrame>
  );
}

/** The application's page-not-found screen for signed-out visitors. */
export function PageNotFound() {
  useDocumentTitle('Page not found');
  return (
    <SignedOutFrame>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold tracking-tight">Page not found</h1>
        <p className="text-muted-foreground text-sm">This page does not exist or is not available.</p>
      </div>
      <Button asChild size="lg"><Link to="/">Go to the sign-in page</Link></Button>
    </SignedOutFrame>
  );
}

export function PublicPagesLoading() {
  return <div role="status" className="bg-background text-muted-foreground flex min-h-svh items-center justify-center p-6 text-sm">Loading…</div>;
}
